import { computed, observable, action, reaction, runInAction } from 'mobx';
import { message } from 'antd';
import { TrainingProjectsStore } from '.';
import { TrainingProjectsService } from '../services';
import { TrainingProjectExampleModel, TrainingProjectSessionModel } from '../models';
import {
    CreateEditTrainingProjectExampleFormValues,
    PreparedPackageForImportDto,
    PackageForImportDto,
    ImportTrainingProjectExamplesResult,
    ExistingExampleMappingDto
} from '../types';
import { YouchamaJsonDiffer } from 'jycm';
import { RouterStore } from '../../common/stores';
import { TrainingProjectsNavigation } from '../routes';
import { showRuntimeSchemaValidationError } from '../utils/runtimeSchemaValidation';

type CreateEditExampleFormValues = CreateEditTrainingProjectExampleFormValues;
type ImportExamplesResult = ImportTrainingProjectExamplesResult;

export default class TrainingProjectDashboardStore {
    @observable
    examples: TrainingProjectExampleModel[] = [];

    @observable
    sessions: TrainingProjectSessionModel[] = [];

    @observable
    isLoadingExamples: boolean = false;

    @observable
    isLoadingSessions: boolean = false;

    @observable
    createEditExampleDialogVisible: boolean = false;

    @observable
    importExamplesDialogVisible: boolean = false;

    @observable
    importReportDialogVisible: boolean = false;

    @observable
    latestImportResult: ImportTrainingProjectExamplesResult | null = null;

    @observable
    isLoadingImportOperations: boolean = false;

    @observable
    availablePackages: PackageForImportDto[] = [];

    @observable
    selectedExampleIds: string[] = [];

    @observable
    isDeletingExamples: boolean = false;

    @observable
    preparedPackagesForImport: PreparedPackageForImportDto[] = [];

    @observable
    isStartingTraining: boolean = false;

    @observable
    streamLogs: Map<string, string[]> = new Map();

    // Dictionary to store JSON schema differences, key is package id, value is json diff object
    @observable
    jsonSchemaDifferences: Map<string, unknown> = new Map();

    @observable
    isValidatingExamples: boolean = false;

    @observable
    examplesPageSize: number = 25;

    readonly sessionPollingIntervals: Map<string, ReturnType<typeof setInterval>> = new Map();
    readonly sessionElapsedTimeIntervals: Map<string, ReturnType<typeof setInterval>> = new Map();

    constructor(
        private readonly trainingProjectsStore: TrainingProjectsStore,
        private readonly service: TrainingProjectsService,
        private readonly routerStore: RouterStore
    ) {
        reaction(
            () => this.trainingProject,
            () => {
                if (this.trainingProject) {
                    this.dispose();
                    this.initialize();
                }
            }
        );
    }

    @computed
    get trainingProject() {
        return this.trainingProjectsStore.selectedTrainingProject;
    }

    @computed
    get project() {
        return this.trainingProjectsStore.project;
    }

    @computed
    get isLoading() {
        return this.trainingProjectsStore.isLoading;
    }

    @computed
    get isLoadingPackages() {
        return this.trainingProjectsStore.isLoadingPackages;
    }

    @computed
    get packageOptions() {
        return this.trainingProjectsStore.packageOptions;
    }

    @computed
    get editableExample() {
        return this.examples.find(example => example.isEditable);
    }

    @computed
    get hasActiveTrainingSession() {
        return this.sessions.some(session => session.status === 'Draft' || session.status === 'Running');
    }

    @action.bound
    goBackToList() {
        if (!this.project) {
            return;
        }

        this.routerStore.push(TrainingProjectsNavigation.TrainingProjectsScreen.replace(':projectId', this.project.id));
    }

    @action.bound
    setCreateEditExampleDialogVisible(createEditExampleDialogVisible: boolean) {
        this.createEditExampleDialogVisible = createEditExampleDialogVisible;
    }

    @action.bound
    setImportExamplesDialogVisible(importExamplesDialogVisible: boolean) {
        this.importExamplesDialogVisible = importExamplesDialogVisible;
    }

    @action.bound
    setImportReportDialogVisible(importReportDialogVisible: boolean) {
        this.importReportDialogVisible = importReportDialogVisible;
    }

    @action.bound
    showImportReport(importResult: ImportExamplesResult) {
        this.latestImportResult = importResult;
        this.importReportDialogVisible = true;
    }

    @action.bound
    clearJsonSchemaDifferences() {
        this.jsonSchemaDifferences.clear();
    }

    @action.bound
    removeTrainingProjectExample(trainingProjectExample: TrainingProjectExampleModel) {
        const newExamples = [...this.examples];
        newExamples.splice(newExamples.indexOf(trainingProjectExample), 1);
        this.examples = newExamples;
    }

    @action.bound
    setSelectedExampleIds(selectedIds: string[]) {
        this.selectedExampleIds = selectedIds;
    }

    @action.bound
    setExamplesPageSize(pageSize: number) {
        this.examplesPageSize = pageSize;
    }

    @action.bound
    clearSelectedExampleIds() {
        this.selectedExampleIds = [];
    }

    @action.bound
    async deleteSelectedExamples() {
        if (!this.project || !this.trainingProject || this.selectedExampleIds.length === 0) {
            return;
        }

        try {
            runInAction(() => {
                this.isDeletingExamples = true;
            });

            const resp = await this.service.deleteTrainingProjectExamplesBulk(
                this.project.id,
                this.trainingProject.id,
                this.selectedExampleIds
            );

            if (!resp.isOk()) {
                message.error('Failed to delete training project examples');
                return;
            }

            message.success(`Successfully deleted ${this.selectedExampleIds.length} example(s)`);

            runInAction(() => {
                this.examples = this.examples.filter(example => !this.selectedExampleIds.includes(example.id));
                this.clearSelectedExampleIds();
            });
        } catch (error) {
            console.error(error);
            message.error('Failed to delete training project examples');
        } finally {
            runInAction(() => {
                this.isDeletingExamples = false;
            });
        }
    }

    @action.bound
    startStreamingLogs(sessionId: string) {
        if (this.streamLogs.has(sessionId)) {
            return;
        }

        this.streamLogs.set(sessionId, []);

        const onMessage = (data: { message: string }) => {
            runInAction(() => {
                const sessionLogs = this.streamLogs.get(sessionId) ?? [];
                sessionLogs.push(data.message);
                this.streamLogs.set(sessionId, sessionLogs);
            });
        };

        const onError = () => {
            message.error('Failed to stream training logs');
        };

        this.service.streamTrainingLogs(sessionId, onMessage, onError);
    }

    @action.bound
    async loadAvailablePackages() {
        try {
            if (!this.project) {
                return;
            }

            runInAction(() => {
                this.isLoadingImportOperations = true;
            });

            const packages = await this.service.getProjectPackages(this.project.id);

            runInAction(() => {
                this.availablePackages = packages;
            });
        } catch (error) {
            console.error(error);
            message.error('Failed to load available packages');
        } finally {
            runInAction(() => {
                this.isLoadingImportOperations = false;
            });
        }
    }

    @action.bound
    async preparePackagesForImport(packageIds: string[]) {
        try {
            if (!this.project || !this.trainingProject) {
                return false;
            }

            runInAction(() => {
                this.isLoadingImportOperations = true;
            });

            const response = await this.service.preparePackagesForImport(
                this.project.id,
                this.trainingProject.id,
                packageIds
            );

            if (!response.isOk()) {
                message.error('Failed to prepare packages for import');
                return false;
            }

            response.map(preparedPackages => {
                runInAction(() => {
                    this.preparedPackagesForImport = preparedPackages;
                });
            });

            this.compareExpectedOutuputsWithSchema();

            return true;
        } catch (error) {
            console.error(error);
            message.error('Failed to prepare packages for import');
            return false;
        } finally {
            runInAction(() => {
                this.isLoadingImportOperations = false;
            });
        }
    }

    @action.bound
    async importExamplesFromPackages(packageIds: string[], existingExampleMappings?: ExistingExampleMappingDto[]) {
        try {
            if (!this.project || !this.trainingProject) {
                return false;
            }

            runInAction(() => {
                this.isLoadingImportOperations = true;
            });

            const response = await this.service.createExamplesFromPackages(
                this.project.id,
                this.trainingProject.id,
                packageIds,
                existingExampleMappings
            );

            if (!response.isOk()) {
                message.error('Failed to import examples from packages');
                return false;
            }

            runInAction(() => {
                this.isLoadingImportOperations = false;
            });

            // Reload examples after successful import
            await this.loadExamples();

            message.success('Examples imported successfully');
            return true;
        } catch (error) {
            console.error(error);
            message.error('Failed to import examples from packages');
            return false;
        } finally {
            runInAction(() => {
                this.isLoadingImportOperations = false;
            });
        }
    }

    @action.bound
    async exportTrainingProjectExamples() {
        try {
            if (!this.project || !this.trainingProject) {
                message.error('Project or training project not available');
                return false;
            }

            // Export examples directly triggers download via service
            await this.service.exportTrainingProjectExamples(this.project.id, this.trainingProject.id);

            message.success('Export started - check your downloads folder');
            return true;
        } catch (error) {
            console.error(error);
            message.error('Failed to export training project examples');
            return false;
        }
    }

    @action.bound
    async exportTrainingProject() {
        try {
            if (!this.project || !this.trainingProject) {
                message.error('Project or training project not available');
                return false;
            }

            await this.service.exportTrainingProject(this.project.id, this.trainingProject.id);

            message.success('Training project export started - check your downloads folder');
            return true;
        } catch (error) {
            console.error(error);
            message.error('Failed to export training project');
            return false;
        }
    }

    @action.bound
    async importTrainingProjectExamples(file: File) {
        try {
            if (!this.project || !this.trainingProject) {
                message.error('Project or training project not available');
                return false;
            }

            runInAction(() => {
                this.isLoadingImportOperations = true;
            });

            const result = await this.service.importTrainingProjectExamples(
                this.project.id,
                this.trainingProject.id,
                file
            );

            // Set loading to false after import operation completes successfully
            runInAction(() => {
                this.isLoadingImportOperations = false;
            });

            // Show detailed import report dialog
            this.showImportReport(result);

            // Reload examples after successful import (separate from import loading state)
            if (result.importedCount > 0) {
                await this.loadExamples();
            }

            return true;
        } catch (error) {
            console.error(error);
            message.error('Failed to import training project examples');
            return false;
        } finally {
            // Ensure loading state is always reset, even if there was an error
            runInAction(() => {
                this.isLoadingImportOperations = false;
            });
        }
    }

    @action.bound
    compareExpectedOutuputsWithSchema() {
        if (
            !this.trainingProject ||
            !this.trainingProject.jsonSchemas ||
            Object.keys(this.trainingProject.jsonSchemas).length === 0
        ) {
            return;
        }

        const newPreparedPackages = [...this.preparedPackagesForImport];
        const newJsonSchemaDifferences = new Map<string, unknown>();

        for (const preparedPackage of newPreparedPackages) {
            // TODO: support multiple schemas (???)
            const firstSchemaKey = Object.keys(this.trainingProject.jsonSchemas)[0];
            const outputSchema = this.trainingProject.jsonSchemas[firstSchemaKey];
            const jsonDiff = this.getJsonDiff(outputSchema, preparedPackage.expectedOutputSchema);

            // Store the JSON difference for this package
            newJsonSchemaDifferences.set(preparedPackage.packageId, jsonDiff);

            const hasMissingFields = jsonDiff['dict:add'] && jsonDiff['dict:add'].length > 0;
            const hasExtraFields = jsonDiff['dict:remove'] && jsonDiff['dict:remove'].length > 0;
            if (hasMissingFields || hasExtraFields) {
                preparedPackage.hasInvalidSchema = true;
            }
        }

        runInAction(() => {
            this.preparedPackagesForImport = newPreparedPackages;
            this.jsonSchemaDifferences = newJsonSchemaDifferences;
        });
    }

    @action.bound
    getJsonDiff(json1: string, json2: string) {
        let jsonObject1, jsonObject2;
        try {
            jsonObject1 = JSON.parse(json1);
        } catch (e) {
            message.error('Failed to parse first JSON object for schema comparison: ' + (e.message || e));
            return {};
        }
        try {
            jsonObject2 = JSON.parse(json2);
        } catch (e) {
            message.error('Failed to parse second JSON object for schema comparison: ' + (e.message || e));
            return {};
        }
        const differ = new YouchamaJsonDiffer(jsonObject1, jsonObject2);
        const diff = differ.get_diff();

        // Remove all keys in "dict:add" and "dict:remove" that start with "definitions->" or contains "debug_reasoning_log"
        if (diff['dict:add']) {
            diff['dict:add'] = diff['dict:add'].filter(
                (item: { right_path: string }) =>
                    !item.right_path.startsWith('definitions->') && !item.right_path.includes('debug_reasoning_log')
            );
        }
        if (diff['dict:remove']) {
            diff['dict:remove'] = diff['dict:remove'].filter(
                (item: { left_path: string }) =>
                    !item.left_path.startsWith('definitions->') && !item.left_path.includes('debug_reasoning_log')
            );
        }

        return diff;
    }

    @action.bound
    async createExample(formValues: CreateEditExampleFormValues) {
        if (!this.project || !this.trainingProject) {
            return;
        }

        const resp = await this.service.createTrainingProjectExample(
            this.project.id,
            this.trainingProject.id,
            formValues
        );

        if (!resp.isOk()) {
            message.error('Failed to create training project example');
            return false;
        }

        resp.map(data => {
            runInAction(() => {
                const newExamples = [...this.examples];
                newExamples.push(new TrainingProjectExampleModel(this, this.service, data));
                this.examples = newExamples;
            });
        });

        return true;
    }

    @action.bound
    async loadExamples() {
        try {
            if (!this.project || !this.trainingProject) {
                return;
            }

            runInAction(() => {
                this.isLoadingExamples = true;
            });

            const examples = await this.service.getTrainingProjectExamples(this.project.id, this.trainingProject.id);

            runInAction(() => {
                this.examples = examples.map(example => new TrainingProjectExampleModel(this, this.service, example));
            });
        } catch (error) {
            console.error(error);
            message.error('Failed to load training project examples');
        } finally {
            runInAction(() => {
                this.isLoadingExamples = false;
            });
        }
    }

    @action.bound
    async validateExamples() {
        if (this.isValidatingExamples || !this.project || !this.trainingProject) {
            return;
        }

        const jsonExamples = this.examples.filter(example => example.outputType === 'Json');

        if (jsonExamples.length === 0) {
            message.info('No JSON examples to validate');
            return;
        }

        runInAction(() => {
            this.isValidatingExamples = true;
        });

        try {
            jsonExamples.forEach(example => {
                example.updateValidationState(undefined, []);
            });

            const resp = await this.service.validateTrainingProjectExamplesJson(
                this.project.id,
                this.trainingProject.id
            );

            if (resp.isOk()) {
                resp.map(result => {
                    result.results.forEach(validationResult => {
                        const example = jsonExamples.find(ex => ex.id === validationResult.id);
                        if (example) {
                            example.updateValidationState(validationResult.isValid, validationResult.errors);
                        } else {
                            console.warn('Could not find example with id:', validationResult.id);
                        }
                    });

                    const validCount = result.results.filter(r => r.isValid === true).length;
                    const invalidCount = result.results.filter(r => r.isValid === false).length;

                    if (invalidCount === 0) {
                        message.success(`All ${validCount} JSON examples are valid`);
                    } else {
                        message.warning(`${validCount} valid, ${invalidCount} invalid JSON examples`);
                    }
                });

                this.refreshExamples();
            } else {
                console.error('Bulk validation failed:', resp);
                if (!showRuntimeSchemaValidationError(resp.error.data)) {
                    message.error('Failed to validate examples');
                }
            }
        } catch (error) {
            console.error('Validation failed:', error);
            message.error('Failed to validate examples');
        } finally {
            runInAction(() => {
                this.isValidatingExamples = false;
            });
        }
    }

    @action.bound
    async loadSessions() {
        try {
            if (!this.project || !this.trainingProject) {
                return;
            }

            runInAction(() => {
                this.isLoadingSessions = true;
            });

            const sessions = await this.service.getTrainingProjectSessions(this.project.id, this.trainingProject.id);

            runInAction(() => {
                this.sessions = sessions.map(session => new TrainingProjectSessionModel(this, this.service, session));
            });

            runInAction(() => {
                this.isLoadingSessions = false;
            });
        } catch (error) {
            console.error(error);
            message.error('Failed to load training project sessions');
        } finally {
            runInAction(() => {
                this.isLoadingSessions = false;
            });
        }
    }

    @action.bound
    async loadTrainingSession(trainingSessionId: string) {
        if (!this.project || !this.trainingProject) {
            return;
        }

        const resp = await this.service.getTrainingSession(this.project.id, this.trainingProject.id, trainingSessionId);

        if (!resp.isOk()) {
            message.error('Failed to get training session');
            return;
        }

        resp.map(data => {
            runInAction(() => {
                this.sessions.push(new TrainingProjectSessionModel(this, this.service, data));
            });
        });
    }

    @action.bound
    async startTraining() {
        if (!this.trainingProject || this.isStartingTraining) {
            return;
        }

        // Disable button immediately to prevent multiple clicks
        runInAction(() => {
            this.isStartingTraining = true;
        });

        try {
            const response = await this.service.startTraining(this.trainingProject.id);

            if (!response.isOk()) {
                message.error('Failed to start training');
                return;
            }

            response.map(trainingSessionId => this.loadTrainingSession(trainingSessionId));
        } catch (error) {
            console.error(error);
            message.error('Failed to start training');
        } finally {
            // Re-enable button after operation completes (success or failure)
            runInAction(() => {
                this.isStartingTraining = false;
            });
        }
    }

    // Otherwise antd table will not be updated
    refreshExamples() {
        runInAction(() => {
            this.examples = [...this.examples];
        });
    }

    loadAllProjectPackages() {
        this.trainingProjectsStore.loadAllProjectPackages();
    }

    addSessionPollingInterval(sessionId: string, interval: ReturnType<typeof setInterval>) {
        this.removeSessionPollingInterval(sessionId);
        this.sessionPollingIntervals.set(sessionId, interval);
    }

    removeSessionPollingInterval(sessionId: string) {
        const interval = this.sessionPollingIntervals.get(sessionId);

        if (interval) {
            clearInterval(interval);
            this.sessionPollingIntervals.delete(sessionId);
        }
    }

    clearSessionPollingIntervals() {
        this.sessionPollingIntervals.forEach((interval, sessionId) => {
            clearInterval(interval);
            this.sessionPollingIntervals.delete(sessionId);
        });
    }

    addSessionElapsedTimeInterval(sessionId: string, interval: ReturnType<typeof setInterval>) {
        this.removeSessionElapsedTimeInterval(sessionId);
        this.sessionElapsedTimeIntervals.set(sessionId, interval);
    }

    removeSessionElapsedTimeInterval(sessionId: string) {
        const interval = this.sessionElapsedTimeIntervals.get(sessionId);

        if (interval) {
            clearInterval(interval);
            this.sessionElapsedTimeIntervals.delete(sessionId);
        }
    }

    clearSessionElapsedTimeIntervals() {
        this.sessionElapsedTimeIntervals.forEach((interval, sessionId) => {
            clearInterval(interval);
            this.sessionElapsedTimeIntervals.delete(sessionId);
        });
    }

    getUserNameById(userId: string) {
        return this.trainingProjectsStore.getUserNameById(userId);
    }

    initialize() {
        this.loadExamples();
        this.loadSessions();
    }

    dispose() {
        runInAction(() => {
            this.examples = [];
            this.sessions = [];
            this.isLoadingExamples = false;
            this.isLoadingSessions = false;
            this.isLoadingImportOperations = false;
            this.availablePackages = [];
            this.preparedPackagesForImport = [];
            this.isStartingTraining = false;
            this.importReportDialogVisible = false;
            this.latestImportResult = null;
            this.isValidatingExamples = false;
        });

        // Clear JSON schema differences storage
        this.clearJsonSchemaDifferences();
        this.clearSessionPollingIntervals();
        this.clearSessionElapsedTimeIntervals();
    }
}
